import React, { useEffect, useState } from "react";
import * as Constants from "../logic/Constants";
import * as WidgetUtils from "../logic/WidgetUtils";

// Externals
import cn from "classnames";

// DevExtreme components
import DataGrid, { Column, ColumnChooser, Export, Grouping, Paging } from 'devextreme-react/data-grid';
import { Toolbar, Item } from 'devextreme-react/toolbar';

// Component styles
import classes from "./TableWidget.module.scss";

export function TableWidget(props) {
  const { ui, data, config, doAction } = props;
  const [isTableSorted, setIsTableSorted] = useState(false);

  //let dataGridSelector;

  let widgetId = ui.id;
  let content = data ? JSON.parse(data.content) : null;
  let tableRows = content && content.DataRows ? content.DataRows : [];
  let enabledTableActions = content && content.EnabledTableActions ? content.EnabledTableActions : [];
  let selectedRowId = content && content.SelectedRowId;
  //let sortedColumnId = content && content.SortedColumnId ? content.SortedColumnId : "";
  //let sortOrder = content && content.SortOrder ? content.SortOrder : "";

  let disabledToolbar = ui.readonly;
  let toolbarButtons = config && config.Actions ? config.Actions.filter(isToolbarAction) : [];
  let tableColumns = config && config.Columns ? config.Columns : [];

  let toolbarHeight = Constants.DEFAULT_ROW_HEIGHT + "em";
  let tableHeight = "calc(100% - " + toolbarHeight + ")";

  const createDataSource = () => {
    return tableRows.map((row) => {
      let newRow = { "rowId": row.RowId };
      tableColumns.forEach((column, index) => newRow[column.ColumnId] = row.DataCells[index]);
      return newRow;
    });
  };

  useEffect(() => {
    console.log("TableWidget data of " + ui.id + ": ", data);
  }, [data]);

  function isToolbarAction(action) {
    switch (action.ActionId) {
      case Constants.TABLE_WIDGET_NEW_ACTION:
      case Constants.TABLE_WIDGET_DELETE_ACTION:
      case Constants.TABLE_WIDGET_MOVEUP_ACTION:
      case Constants.TABLE_WIDGET_MOVEDOWN_ACTION:
        return true;
      default:
        return false;
    }
  }

  const toCellAlignment = align => {
    switch (align) {
      case 2:
        return "right";
      case 1:
        return "center";
      default:
        return "left";
    }
  };

  const toColumnDataType = type => {
    switch (type) {
      case 3:   //action
        return null;
      case 2:   //string
        return "string";
      case 1:   //double
        return "number"
      case 0:   //integer
        return "number"
      default:
        return "string";
    }
  };

  const toColumnWidth = (column) => {
    return column.FixedWidth ? pxWidthFromPercentage(column.Width) : column.Width + "%"
  }

  const pxWidthFromPercentage = (perc) => {
    let fullWidth = ui.controlWidth;
    let result = (fullWidth / 100) * perc;
    return WidgetUtils.toPX(result) + "px";
  };

  const findIcon = actionId => {
    switch (actionId) {
      case Constants.TABLE_WIDGET_NEW_ACTION:
        return "plus";
      case Constants.TABLE_WIDGET_DELETE_ACTION:
        return "close";
      case Constants.TABLE_WIDGET_MOVEUP_ACTION:
        return "arrowup";
      case Constants.TABLE_WIDGET_MOVEDOWN_ACTION:
        return "arrowdown";
      default:
        return <React.Fragment />;
    }
  };

  const onActionButtonClick = async (actionId) => {
    await doTableAction(actionId, selectedRowId);
  };

  const onSelectionChanged = async (event) => {
    let rowId = event.selectedRowKeys[0];
    if (rowId) {
      await doTableAction(Constants.TABLE_WIDGET_SELECT_ACTION, rowId);
    }
  };

  const onRowDoubleClicked = async (event) => {
    if (!isActionAllowed(Constants.TABLE_WIDGET_OPEN_ACTION)) return;
    let rowId = event.key;
    if (rowId) {
      await doTableAction(Constants.TABLE_WIDGET_OPEN_ACTION, rowId);
    }
  };

  const onOptionChanged = (event) => {
    switch (event.name) {
      case "columns":
        let isSorted = event.value !== undefined && event.value !== 0;
        setIsTableSorted(isSorted);
        break;
      default:
        break;
    }
  };

  const onContentReady = (args) => {
    //console.log("CONTENT READY", args);
    args.component.navigateToRow(selectedRowId);
  };

  const doTableAction = async (actionId, data) => {
    console.log("Executing TableWidget Action:", actionId);
    await doAction(actionId, data);
  };

  const isActionAllowed = (actionId) => {
    if (disabledToolbar && isToolbarAction(actionId)) return false;
    if (isTableSorted) return false;
    return enabledTableActions.includes(actionId);
  };

  const renderActionButton = (control) => {
    return (
      <Item
        key={control.ActionId}
        widget="dxButton"
        location="after"
        disabled={!isActionAllowed(control.ActionId)}
        options={{
          icon: findIcon(control.ActionId),
          hint: control.Tooltip,
          stylingMode: "text",
          onClick: () => onActionButtonClick(control.ActionId)
        }}
      />
    );
  };

  const renderTableLabel = () => {
    return (
      <Item
        key={'tableLabel'}
        text={WidgetUtils.toCleanText(ui.label)}
        location="before"
      />
    );
  };

  // const renderColumnChooserButton = () => {
  //   return (
  //     <Item
  //       key={'columnChooser'}
  //       widget="dxButton"
  //       location="after"
  //       options={{
  //         icon: 'column-chooser',
  //         hint: "Select columns to show or hide",
  //         stylingMode: "text",
  //         onClick: () => dataGridSelector.instance.showColumnChooser()
  //       }}
  //     />
  //   );
  // };

  // const renderExportButton = () => {
  //   return (
  //     <Item
  //       key={'export'}
  //       widget="dxButton"
  //       location="after"
  //       options={{
  //         icon: 'xlsxfile',
  //         hint: "Export to Excel",
  //         stylingMode: "text",
  //         onClick: () => dataGridSelector.instance.exportToExcel()
  //       }}
  //     />
  //   );
  // };

  const renderCommandBar = () => {
    return (
      <Toolbar className={cn(classes.toolbar, "dx-theme-border-color")} style={{ height: toolbarHeight }}>
        {renderTableLabel()}
        {toolbarButtons.map(control => (
          renderActionButton(control)
        ))}
        {/* {renderColumnChooserButton()} */}
        {/* {renderExportButton()} */}
      </Toolbar>
    );
  };

  const renderTable = () => {
    return (
      <DataGrid
        dataSource={createDataSource()}
        showBorders={true}
        showColumnLines={false}
        showRowLines={true}
        rowAlternationEnabled={true}
        hoverStateEnabled={true}
        allowColumnReordering={true}
        allowColumnResizing={true}
        selection={{ mode: "single" }}
        keyExpr="rowId"
        defaultSelectedRowKeys={[selectedRowId]}
        onSelectionChanged={onSelectionChanged}
        onRowDblClick={onRowDoubleClicked}
        onOptionChanged={onOptionChanged}
        onContentReady={(e) => onContentReady(e)}
        // ref={el => dataGridSelector = el}
        style={{ height: tableHeight }}
      >
        <ColumnChooser enabled={false} mode="select" />
        <Export enabled={false} fileName={WidgetUtils.toCleanText(ui.label)} />
        <Grouping visible={false} contextMenuEnabled={true} />
        <Paging defaultPageSize={100} />

        {tableColumns.map((column) => (
          <Column
            key={column.ColumnId}
            caption={column.HeaderText}
            dataField={column.ColumnId}
            allowSorting={true}
            allowHiding={true}
            alignment={toCellAlignment(column.Alignment)}
            dataType={toColumnDataType(column.DataType)}
            width={toColumnWidth(column)}
            format={column.Format}
          />
        ))}
      </DataGrid>
    );
  };

  return config ? (
    <div className={classes.wrapper} id={widgetId}>
      {renderCommandBar()}
      {renderTable()}
    </div>
  ) : null;
}
